/**
 * Utility functions for quantity validation and manipulation
 * Production ready with comprehensive error handling and validation
 */

/**
 * Clamp a number between min and max values
 * @param {number} n - The number to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Clamped number
 */
export function clamp(n, min, max) {
  try {
    // Validate inputs
    if (!isFinite(n) || !isFinite(min) || !isFinite(max)) {
      throw new Error('All parameters must be finite numbers');
    }

    if (min > max) {
      throw new Error('Min value cannot be greater than max value');
    }

    return Math.min(Math.max(n, min), max);
  } catch (error) {
    console.error('Error in clamp function:', error);
    // Return safe fallback
    return Math.max(10, min);
  }
}

/**
 * Round a number to the nearest increment
 * @param {number} n - The number to round
 * @param {number} step - The increment step
 * @returns {number} Rounded number
 */
export function roundToIncrement(n, step) {
  try {
    // Validate inputs
    if (!isFinite(n) || !isFinite(step)) {
      throw new Error('All parameters must be finite numbers');
    }

    if (step <= 0) {
      return n;
    }

    return Math.round(n / step) * step;
  } catch (error) {
    console.error('Error in roundToIncrement function:', error);
    // Return safe fallback
    return Math.round(n);
  }
}

/**
 * Sanitize raw input string to a valid number
 * @param {string} raw - Raw input string
 * @returns {number|null} Parsed number or null if invalid
 */
export function sanitize(raw) {
  try {
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    // Remove spaces and replace comma with dot for decimal parsing
    const cleaned = raw.replace(/\s/g, '').replace(',', '.');

    // Parse as float first to handle decimal numbers
    const parsed = parseFloat(cleaned);

    // Check if it's a valid number and convert to integer
    if (isNaN(parsed) || !isFinite(parsed)) {
      return null;
    }

    // Return as integer (truncate decimals)
    return Math.trunc(parsed);
  } catch (error) {
    console.error('Error in sanitize function:', error);
    return null;
  }
}

/**
 * Validate quantity input and return corrected value if needed
 * @param {string|number} input - Raw input value
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @param {number} step - Increment step
 * @param {boolean} enforceIncrement - Whether to enforce increment rounding
 * @returns {Object} Object with validation results
 */
export function validateQuantity(
  input,
  min = 10,
  max = 99999,
  step = 1,
  enforceIncrement = false,
) {
  try {
    // Input validation
    if (input === null || input === undefined) {
      return {
        value: min,
        isValid: false,
        corrected: true,
        reason: 'empty_input',
        originalInput: input,
        error: null,
      };
    }

    // Validate min/max/step parameters
    if (!isFinite(min) || !isFinite(max) || !isFinite(step)) {
      throw new Error('Min, max, and step must be finite numbers');
    }

    if (min < 10 || max <= 0 || step <= 0) {
      throw new Error('Min must be >= 10, max must be > 0, step must be > 0');
    }

    if (min >= max) {
      throw new Error('Min must be less than max');
    }

    const originalInput = input;

    // Convert to string if number
    const inputStr = typeof input === 'number' ? input.toString() : input;

    // Handle empty input
    if (!inputStr.trim()) {
      return {
        value: min,
        isValid: false,
        corrected: true,
        reason: 'empty_input',
        originalInput,
        error: null,
      };
    }

    // Sanitize input
    const sanitized = sanitize(inputStr);
    if (sanitized === null) {
      return {
        value: min,
        isValid: false,
        corrected: true,
        reason: 'invalid_format',
        originalInput,
        error: null,
      };
    }

    let finalValue = sanitized;
    let corrected = false;
    let reason;

    // Clamp to min/max
    if (finalValue < min) {
      finalValue = min;
      corrected = true;
      reason = 'below_min';
    } else if (finalValue > max) {
      finalValue = max;
      corrected = true;
      reason = 'above_max';
    }

    // Round to increment if enforced
    if (enforceIncrement && step > 1) {
      const rounded = roundToIncrement(finalValue, step);
      if (rounded !== finalValue) {
        finalValue = rounded;
        corrected = true;
        reason = 'increment_rounded';
      }
    }

    return {
      value: finalValue,
      isValid: true,
      corrected,
      reason,
      originalInput,
      error: null,
    };
  } catch (error) {
    console.error('Error in validateQuantity function:', error);

    // Return safe fallback with error information
    return {
      value: Math.max(10, min),
      isValid: false,
      corrected: true,
      reason: 'validation_error',
      originalInput: input,
      error: error.message,
    };
  }
}

/**
 * Format number with thousand separators (Dutch format)
 * @param {number} n - Number to format
 * @param {string} separator - Thousand separator (default: '.')
 * @returns {string} Formatted number string
 */
export function formatNumber(n, separator = '.') {
  try {
    if (!isFinite(n)) {
      return '0';
    }

    const numStr = Math.abs(n).toString();
    const parts = [];

    // Split into groups of 3 from right
    for (let i = numStr.length; i > 0; i -= 3) {
      parts.unshift(numStr.slice(Math.max(0, i - 3), i));
    }

    const formatted = parts.join(separator);
    return n < 0 ? `-${formatted}` : formatted;
  } catch (error) {
    console.error('Error in formatNumber function:', error);
    return n.toString();
  }
}

/**
 * Parse formatted number string back to number
 * @param {string} formatted - Formatted number string
 * @param {string} separator - Thousand separator (default: '.')
 * @returns {number|null} Parsed number or null if invalid
 */
export function parseFormattedNumber(formatted, separator = '.') {
  try {
    if (!formatted || typeof formatted !== 'string') {
      return null;
    }

    // Remove separators and parse
    const cleaned = formatted.replace(new RegExp(`\\${separator}`, 'g'), '');
    const parsed = parseFloat(cleaned);

    return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
  } catch (error) {
    console.error('Error in parseFormattedNumber function:', error);
    return null;
  }
}

/**
 * Check if a value is within valid range
 * @param {number} value - Value to check
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {boolean} True if value is within range
 */
export function isInRange(value, min, max) {
  try {
    if (!isFinite(value) || !isFinite(min) || !isFinite(max)) {
      return false;
    }

    return value >= min && value <= max;
  } catch (error) {
    console.error('Error in isInRange function:', error);
    return false;
  }
}

/**
 * Calculate the next valid increment value
 * @param {number} current - Current value
 * @param {number} step - Increment step
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {number} Next valid increment value
 */
export function getNextIncrement(current, step, min, max) {
  try {
    if (
      !isFinite(current) ||
      !isFinite(step) ||
      !isFinite(min) ||
      !isFinite(max)
    ) {
      throw new Error('All parameters must be finite numbers');
    }

    if (step <= 0 || min >= max) {
      throw new Error('Invalid step or range parameters');
    }

    const next = roundToIncrement(current + step, step);
    return clamp(next, min, max);
  } catch (error) {
    console.error('Error in getNextIncrement function:', error);
    return clamp(current + 1, min, max);
  }
}

/**
 * Calculate the previous valid increment value
 * @param {number} current - Current value
 * @param {number} step - Increment step
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {number} Previous valid increment value
 */
export function getPreviousIncrement(current, step, min, max) {
  try {
    if (
      !isFinite(current) ||
      !isFinite(step) ||
      !isFinite(min) ||
      !isFinite(max)
    ) {
      throw new Error('All parameters must be finite numbers');
    }

    if (step <= 0 || min >= max) {
      throw new Error('Invalid step or range parameters');
    }

    const prev = roundToIncrement(current - step, step);
    return clamp(prev, min, max);
  } catch (error) {
    console.error('Error in getPreviousIncrement function:', error);
    return clamp(current - 1, min, max);
  }
}
